
import React, { useMemo, useEffect, useRef } from 'react';
import { MealSession, SymptomSession, Language } from '../types';

interface MonthlyCalendarProps {
  sessions: (MealSession | SymptomSession)[];
  onSelectDate: (date: Date) => void;
  selectedDate: Date;
  type: 'meal' | 'symptom';
  language: Language;
}

const MonthlyCalendar: React.FC<MonthlyCalendarProps> = ({ sessions, onSelectDate, selectedDate, type, language }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Generate last 12 months for scrolling (Reversed: Oldest -> Newest)
  const months = useMemo(() => {
    const result = [];
    const current = new Date();
    // Start from current month and go back 12 months
    const startDate = new Date(current.getFullYear(), current.getMonth(), 1);
    
    for (let i = 0; i < 13; i++) {
        const d = new Date(startDate);
        d.setMonth(d.getMonth() - i);
        result.push(d);
    }
    return result.reverse(); // Chronological: Past at top, Future (Current) at bottom
  }, []);

  // Scroll to bottom on mount to show current month
  useEffect(() => {
      if (containerRef.current) {
          containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
  }, []);

  // Map sessions to simple date strings for O(1) lookup
  const sessionMap = useMemo(() => {
      const map: Record<string, number> = {};
      sessions.forEach(s => {
          const key = new Date(s.timestamp).toDateString();
          map[key] = (map[key] || 0) + 1;
      });
      return map;
  }, [sessions]);

  // Colors
  const dotColor = type === 'meal' ? 'bg-emerald-500' : 'bg-orange-500';
  const selectedBg = type === 'meal' ? 'bg-emerald-600' : 'bg-orange-500';
  const todayRing = type === 'meal' ? 'ring-emerald-300' : 'ring-orange-300';

  const renderMonth = (monthDate: Date) => {
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth();
      
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const daysInMonth = lastDay.getDate();
      
      // Adjust start day to Monday (0=Mon, 6=Sun)
      let startDay = firstDay.getDay() - 1;
      if (startDay === -1) startDay = 6;

      const days = [];
      // Empty slots for start of month
      for (let i = 0; i < startDay; i++) {
          days.push(<div key={`empty-${i}`} className="h-14 w-full"></div>);
      }

      // Actual Days
      for (let i = 1; i <= daysInMonth; i++) {
          const date = new Date(year, month, i);
          const dateStr = date.toDateString();
          const count = sessionMap[dateStr] || 0;
          const isSelected = date.toDateString() === selectedDate.toDateString();
          const isToday = date.toDateString() === new Date().toDateString();

          days.push(
              <button 
                key={i}
                onClick={() => onSelectDate(date)}
                className={`h-14 w-full flex flex-col items-center justify-start pt-1 rounded-lg transition-all relative ${isSelected ? `${selectedBg} text-white shadow-md` : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200'} ${isToday && !isSelected ? `ring-1 ${todayRing} bg-gray-50 dark:bg-gray-800` : ''}`}
              >
                  <span className={`text-xs font-semibold ${isSelected ? 'text-white' : 'text-gray-700 dark:text-gray-300'}`}>{i}</span>
                  
                  {/* Dots Container */}
                  <div className="flex flex-wrap justify-center gap-0.5 mt-1.5 px-1 max-w-[80%]">
                      {count > 0 && Array.from({ length: Math.min(count, 8) }).map((_, idx) => (
                          <div 
                            key={idx} 
                            className={`w-1 h-1 rounded-full ${isSelected ? 'bg-white' : dotColor}`}
                          ></div>
                      ))}
                      {count > 8 && <div className={`w-1 h-1 rounded-full ${isSelected ? 'bg-white' : 'bg-gray-400'}`}></div>}
                  </div>
              </button>
          );
      }

      const monthLabel = monthDate.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { month: 'long', year: 'numeric' });

      return (
          <div key={`${year}-${month}`} className="mb-6">
              <h3 className="text-sm font-bold text-gray-800 dark:text-white capitalize mb-3 ml-2 sticky top-0 bg-white/95 dark:bg-gray-900/95 py-2 z-10 backdrop-blur-sm">{monthLabel}</h3>
              <div className="grid grid-cols-7 gap-1">
                  {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, idx) => (
                      <div key={idx} className="text-center text-[10px] font-bold text-gray-400 mb-1">{d}</div>
                  ))}
                  {days}
              </div>
          </div>
      );
  };

  return (
    <div ref={containerRef} className="animate-slideIn pb-safe px-2 h-full overflow-y-auto">
        {months.map(m => renderMonth(m))}
    </div>
  );
};

export default MonthlyCalendar;
